import { useTranslation } from 'react-i18next';
import { WarningSign } from '../../UI';
import type { UnscheduledIntervention } from '../../../types/weekOverview';
import { SESSION_TYPE_TRANSLATION_KEYS } from '../../../constants/weekOverviewConstants';
import styles from './UnscheduledInterventionsModal.module.css';

interface UnscheduledInterventionItemProps {
  intervention: UnscheduledIntervention;
  patientNameById: Map<string, string>;
}

export const UnscheduledInterventionItem = ({
  intervention,
  patientNameById
}: UnscheduledInterventionItemProps) => {
  const { t } = useTranslation();

  const metaParts: string[] = [];
  if (intervention.sessionType) {
    metaParts.push(t(SESSION_TYPE_TRANSLATION_KEYS[intervention.sessionType]));
  }
  if (intervention.durationMinutes !== null) {
    metaParts.push(
      t('week-overview-unscheduled-duration', { minutes: intervention.durationMinutes })
    );
  }

  const patientNames = intervention.patientIds.map((id) => patientNameById.get(id) ?? id);

  // 0 feasible candidates means no slot could ever fit (a hard constraint
  // problem); a positive count means slots existed but lost out to other
  // bookings. Without a diagnostics entry the solver gave no reason.
  const reasonKey =
    intervention.feasibleCandidateCount === 0
      ? 'week-overview-unscheduled-reason-no-slot'
      : 'week-overview-unscheduled-reason-conflict';
  const reason = intervention.feasibleCandidateCount === null ? null : t(reasonKey);

  return (
    <li className={styles.item}>
      <WarningSign variant="warning" className={styles.itemWarning} />
      <div className={styles.itemBody}>
        <span className={styles.itemTitle}>{intervention.title}</span>
        {metaParts.length > 0 && <span className={styles.itemMeta}>{metaParts.join(' · ')}</span>}
        <span className={styles.itemMeta}>
          {`${t('week-overview-detail-patients')}: `}
          {patientNames.length > 0
            ? patientNames.join(', ')
            : t('week-overview-detail-no-patients')}
        </span>
        {reason && <span className={styles.itemReason}>{reason}</span>}
      </div>
    </li>
  );
};
